import screenerFilters from '~/ui-apps/filterData/screener_filters.json';
import { parseChecklistClause } from '~/utilities/checklistQueryParse';
import { getSignalScreenerQuery, SIGNAL_SCREENER_QUERIES } from '../data/signalScreenerQueries';

interface ScreenerFilterDefinition {
  name: string;
  key: string;
  glossary_key?: string;
  filter_type?: string;
  shortDescription?: string;
}

export interface OnboardingChecklistPayload {
  query: string;
  name: string;
  key: string;
  short_description: string;
  glossary_key?: string;
  filter_type?: string;
}

interface StoredUserChecklistRow {
  id: number;
  query?: string | null;
  key?: string | null;
}

const filterByKey = new Map<string, ScreenerFilterDefinition>();
for (const filter of screenerFilters as ScreenerFilterDefinition[]) {
  if (filter.key) {
    filterByKey.set(filter.key, filter);
  }
}

export const ALL_ONBOARDING_SCREENER_QUERIES = Object.values(SIGNAL_SCREENER_QUERIES).map((query) =>
  query.trim(),
);

export function getOnboardingChecklistFieldKeys(): Set<string> {
  const keys = new Set<string>();
  for (const query of ALL_ONBOARDING_SCREENER_QUERIES) {
    const field = parseChecklistClause(query)?.field;
    if (field) {
      keys.add(field);
    }
  }
  return keys;
}

export function buildChecklistDisplayName(filterName: string, query: string): string {
  const parsed = parseChecklistClause(query);
  if (!parsed) {
    return filterName;
  }

  return `${filterName} ${parsed.operator} ${parsed.value}`.trim();
}

export function buildChecklistItemFromSignal(signalId: string): OnboardingChecklistPayload | null {
  const query = getSignalScreenerQuery(signalId);
  if (!query) {
    return null;
  }

  const fieldKey = parseChecklistClause(query)?.field;
  if (!fieldKey) {
    return null;
  }

  const filterDef = filterByKey.get(fieldKey);
  if (!filterDef) {
    return null;
  }

  return {
    query,
    name: buildChecklistDisplayName(filterDef.name, query),
    key: filterDef.key,
    short_description: filterDef.shortDescription ?? '',
    glossary_key: filterDef.glossary_key,
    filter_type: filterDef.filter_type,
  };
}

export function buildChecklistItemsFromSignals(signalIds: string[]): OnboardingChecklistPayload[] {
  return signalIds
    .map(buildChecklistItemFromSignal)
    .filter((item): item is OnboardingChecklistPayload => item !== null);
}

export function getSignalFilterLabels(signalIds: string[]): string[] {
  return buildChecklistItemsFromSignals(signalIds).map((item) => item.name);
}

export function isOnboardingManagedChecklistRow(row: StoredUserChecklistRow): boolean {
  const onboardingQueries = new Set(ALL_ONBOARDING_SCREENER_QUERIES);
  const onboardingKeys = getOnboardingChecklistFieldKeys();

  const query = row.query?.trim() ?? '';
  const key = row.key?.trim() ?? '';

  return (query.length > 0 && onboardingQueries.has(query)) || (key.length > 0 && onboardingKeys.has(key));
}
